import { action, computed, observable, reaction, runInAction } from 'mobx'
import Scheduler from '@/utils/Scheduler.js'
import { getToastIconComponent } from '@/components/ui/sonner.jsx'
import { mdiAccountMinusOutline, mdiAccountPlusOutline } from '@mdi/js'
import {
  getJitsiConfig,
  getJitsiDomain,
  getJitsiInterfaceConfig,
  JITSI_BALANCE_CHECK_INTERVAL,
} from '@/constants/jitsi.js'
import { WALLET_HOURLY_RATE_WARNING_THRESHOLD } from '@/constants/wallet.js'
import JitsiLowLevelApiStore from '@/stores/jitsi/JitsiLowLevelApiStore.js'
import JitsiIframeApiStore from '@/stores/jitsi/JitsiIframeApiStore.js'
import { isAddress } from 'viem'

export default class JitsiStore {
  rootStore = null

  #jitsiLowLevelApi = null
  #jitsiIframeApi = null

  #reactions = []
  #checkBalanceScheduler = null
  #lowBalanceToastId = null

  @observable accessor post = null
  @observable accessor isPurchase = false
  @observable accessor jwt = ''
  @observable accessor roomName = '' // it's a hash, not the actual room name
  @observable accessor region = ''
  @observable accessor isConnected = false
  @observable accessor users = observable.map()

  constructor(rootStore) {
    this.rootStore = rootStore
    this.rootStore.registerAfterConstructor(this.#_afterConstructor)
    this.rootStore.registerDeconstructor(this.#_deconstructor)

    this.#jitsiLowLevelApi = new JitsiLowLevelApiStore(this)
    this.#jitsiIframeApi = new JitsiIframeApiStore(this)

    this.#checkBalanceScheduler = new Scheduler(
      this.#checkBalance,
      JITSI_BALANCE_CHECK_INTERVAL,
      true
    )
  }

  #_afterConstructor = () => {
    this.#reactions.push(
      reaction(() => [this.auth.user?.avatar, this.auth.user?.nickname], this.#syncLocalUserData)
    )
  }

  #_deconstructor = async () => {
    this.#reactions.forEach(x => x())
    this.#reactions = []
    await this.reset()
    this.#checkBalanceScheduler.deconstructor()
    this.#jitsiLowLevelApi.deconstructor()
    this.#jitsiIframeApi.deconstructor()
  }

  init = async (post, { isPurchase, jwt, roomName, region }) => {
    await this.reset()

    if (this.isInitialized) return

    console.log('[Jitsi] Initializing...')

    runInAction(() => {
      this.post = post
      this.isPurchase = isPurchase
      this.jwt = jwt
      this.roomName = roomName
      this.region = region
    })

    if (!this.post.isMeeting && !this.post.isLivestreamViaJitsi) await this.#jitsiLowLevelApi.init()

    if (this.post.isMeeting || this.post.isLivestream) this.analytics.joinRoom(this.post.id)?.then()
  }

  initIframeApi = jitsiApi => this.#jitsiIframeApi.init(jitsiApi)

  reset = async () => {
    this.#checkBalanceScheduler.reset()

    this.notifications.dismissToast(this.#lowBalanceToastId)
    this.#lowBalanceToastId = null

    this.#jitsiLowLevelApi?.reset()
    this.#jitsiIframeApi?.reset()

    runInAction(() => {
      this.post = null
      this.isPurchase = false
      this.jwt = ''
      this.roomName = ''
      this.region = ''
      this.isConnected = false
      this.users.clear()
    })

    console.log('[Jitsi] Reset')
  }

  get analytics() {
    return this.rootStore.analyticsStore
  }

  get auth() {
    return this.rootStore.authStore
  }

  get notifications() {
    return this.rootStore.notificationsStore
  }

  get router() {
    return this.rootStore.routerStore
  }

  get wallet() {
    return this.rootStore.walletStore
  }

  @computed
  get isInitialized() {
    return this.post !== null
  }

  @computed
  get currentJitsiApi() {
    if (this.#jitsiLowLevelApi?.id) return this.#jitsiLowLevelApi
    if (this.#jitsiIframeApi?.id) return this.#jitsiIframeApi

    return null
  }

  @computed
  get domain() {
    return getJitsiDomain(this.region)
  }

  @computed
  get config() {
    return this.post
      ? getJitsiConfig(
          this.region,
          this.post.isLivestream,
          this.post.isLivestreamViaJitsi && !this.post.user.isAuthUser
        )
      : null
  }

  @computed
  get interfaceConfig() {
    return this.post ? getJitsiInterfaceConfig(this.post.isLivestreamViaJitsi) : null
  }

  @computed
  get usersArray() {
    return Array.from(this.users.values())
  }

  @computed
  get totalUserCount() {
    return this.usersArray.length + 1 // +1 for the local user
  }

  @computed
  get hostUser() {
    if (!this.post) return null

    return this.post.user.isAuthUser
      ? {
          jitsiId: this.currentJitsiApi?.id,
          nickname: this.auth.user.nickname,
        }
      : this.usersArray.find(x => x.nickname === this.post.user.nickname)
  }

  // not computed
  get hourlyRateSecondsElapsed() {
    return this.post?.isHourlyRate
      ? BigInt(
          this.#checkBalanceScheduler.lastRunTimestamp -
            this.#checkBalanceScheduler.firstRunTimestamp
        ) / 1000n
      : 0n
  }

  @action
  onConnected = () => {
    console.log('[Jitsi] Connected')
    this.isConnected = true

    if (this.post.isMeeting || this.post.isLivestreamViaJitsi)
      this.analytics.videoConferenceJoined(this.post.id)?.then()
  }

  onConnectionFailed = error => {
    console.error('[Jitsi] Connection failed', error)
    this.#leaveFatalError().then()
  }

  @action
  onDisconnected = () => {
    console.log('[Jitsi] Disconnected')
    this.isConnected = false

    this.leave().then()
  }

  onRoomJoined = () => {
    console.log('[Jitsi] Joined room')

    if (this.post.isHourlyRate && !this.post.user.isAuthUser)
      this.#checkBalanceScheduler.start(true).then()
  }

  onError = ({ error }) => {
    if (error?.name === 'conference.destroyed') {
      if (this.post.isMeeting || this.post.isLivestreamViaJitsi)
        this.analytics.leaveRoomAtEnd(this.post.id)?.then()

      this.leave().then()

      return
    }

    console.error('[Jitsi] Room error', error)
    this.#leaveFatalError().then()
  }

  onUserJoined = (id, displayName) => {
    this.setUser(id, displayName)
    console.log(`[Jitsi] User ${displayName} joined`)
  }

  @action
  onUserLeft = id => {
    const user = this.users.get(id)
    if (!user) return

    this.users.delete(id)
    console.log(`[Jitsi] User ${user.nickname} left`)

    if (id === this.currentJitsiApi?.id)
      if (this.post.user.isAuthUser) this.analytics.leaveRoomHostHangup(this.post.id)?.then()
      else this.analytics.leaveRoomGuestHangup(this.post.id)?.then()

    if (!this.post.isMeeting && !this.post.isLivestream) return

    if (id === this.currentJitsiApi?.id) {
      this.leave().then()

      return
    }

    this.notifications.toast(`${user.nickname} left.`, {
      icon: getToastIconComponent(mdiAccountMinusOutline),
    })
  }

  @action
  setUser = (id, displayName, allowNotifications = false) => {
    if (id === this.currentJitsiApi?.id || id === 'local' || !displayName) return

    const nickname = this.users.get(id)?.nickname
    // FIXME: hacky workaround because jitsi name is set to the user's address
    if (allowNotifications && (!nickname || isAddress(nickname)))
      this.#showUserJoinedToast(displayName)

    this.users.set(id, {
      jitsiId: id,
      nickname: displayName,
    })
  }

  leave = async () => {
    if (this.post) {
      if (
        this.post.user.isAuthUser &&
        (this.post.isMeeting || this.post.isLivestream) &&
        this.post.isHourlyRate
      )
        await this.currentJitsiApi?.endConference()

      if (this.post.isMeeting || this.post.isLivestream) {
        this.analytics.leaveRoomFirst(this.post.id)?.then()

        await this.router.navigate(this.router.routes.post, {
          ignoreBlocker: true,
          params: { id: this.post.id },
        })
      }
    }

    return this.reset()
  }

  #leaveFatalError = async () => {
    this.notifications.toastError('Failed to access post!')

    return this.leave()
  }

  #showUserJoinedToast = name => {
    if (!isAddress(name) && (this.post.isMeeting || this.post.isLivestream))
      this.notifications.toast(`${name} joined.`, {
        icon: getToastIconComponent(mdiAccountPlusOutline),
      })
  }

  #checkBalance = async () => {
    const secondsCanAfford = this.wallet.balance.amount / this.post.pricePerSecond

    if (secondsCanAfford <= 0n || this.hourlyRateSecondsElapsed >= secondsCanAfford) {
      await this.leave()
      setTimeout(() => {
        this.notifications.toastError('Insufficient balance!')
      }, 500)
      console.log('[Jitsi] Insufficient balance')

      return
    }

    if (secondsCanAfford <= WALLET_HOURLY_RATE_WARNING_THRESHOLD) {
      if (this.#lowBalanceToastId === null)
        this.#lowBalanceToastId = this.notifications.toastWarning('Low balance!', {
          description: 'Your balance is expected to end soon, please top up to continue.',
          dismissable: false,
        })
    } else {
      this.notifications.dismissToast(this.#lowBalanceToastId)
      this.#lowBalanceToastId = null
    }
  }

  #syncLocalUserData = () => this.currentJitsiApi?.syncLocalUserData()
}
